import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Divider, FormControl, FormHelperText, FormLabel,
    Input, Modal, ModalDialog, Option, Select, Sheet, Stack, Textarea, Typography,
} from '@mui/joy';
import UploadRoundedIcon from '@mui/icons-material/UploadRounded';

import { useAdvertiser } from '../../AdvertiserContext';
import { APIAdsCreativeCreate, APIAdsCreativeUpdate, adsErrorText } from '../../api_ads';
import AdCreativePreview from './AdCreativePreview';
import { useAdsPlacements } from './useAdsData';
import { FORMAT_LABELS } from './adsConstants';
import {
    mediaSpecForFormat, strictestTextSpec, validateImage, validateVideo,
} from './mediaValidation';


const EMPTY = {
    format: '', title: '', text: '', cta_text: '', brand_name: '',
    landing_url: '', deeplink: '',
    utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '',
    weight: 100,
};


function CharCounter({ value, max }) {
    const length = (value || '').length;
    const over = length > max;
    return (
        <Typography
            level="body-xs"
            sx={{ color: over ? 'danger.500' : 'neutral.500', textAlign: 'right' }}
        >
            {length} / {max}
        </Typography>
    );
}


/**
 * Создание и редактирование объявления.
 *
 * Слева форма, справа живой предпросмотр в выбранном формате — тот же
 * компонент, который увидит модератор и который повторяет реальный блок.
 * Пока не видишь результат, подобрать заголовок под 40 символов невозможно.
 */
export default function AdCreativeEditor({
    open, onClose, groupId, creative, allowedFormats = [], onSaved,
}) {
    const { advertiserToken: token } = useAdvertiser();
    const { placements } = useAdsPlacements();

    const isEdit = !!creative?.id;
    const [form, setForm] = useState(EMPTY);
    const [imageFile, setImageFile] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [videoDuration, setVideoDuration] = useState(null);

    const [mediaErrors, setMediaErrors] = useState([]);
    const [mediaWarnings, setMediaWarnings] = useState([]);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        if (isEdit) {
            setForm({ ...EMPTY, ...creative });
            setPreviewUrl(creative.image_url || null);
        } else {
            setForm({ ...EMPTY, format: allowedFormats[0] || '' });
            setPreviewUrl(null);
        }
        setImageFile(null);
        setVideoFile(null);
        setVideoDuration(null);
        setMediaErrors([]);
        setMediaWarnings([]);
        setError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, creative?.id]);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const textSpec = useMemo(
        () => strictestTextSpec(placements, form.format),
        [placements, form.format],
    );
    const mediaSpec = useMemo(
        () => mediaSpecForFormat(placements, form.format),
        [placements, form.format],
    );

    const isVideoFormat = form.format === 'rewarded_video';

    const handleImage = async (file) => {
        if (!file) return;
        setMediaErrors([]);
        setMediaWarnings([]);
        const result = await validateImage(file, mediaSpec.image || {});
        setMediaErrors(result.errors);
        setMediaWarnings(result.warnings);
        if (!result.ok) return;
        setImageFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleVideo = async (file) => {
        if (!file) return;
        setMediaErrors([]);
        setMediaWarnings([]);
        const result = await validateVideo(file, mediaSpec.video || {});
        setMediaErrors(result.errors);
        setMediaWarnings(result.warnings);
        if (!result.ok) return;
        setVideoFile(file);
        setVideoDuration(result.meta?.duration || null);
    };

    const validate = () => {
        if (!form.format) return 'Выберите формат объявления';
        if (!form.title.trim()) return 'Заполните заголовок';
        if (form.title.length > textSpec.title_max) {
            return `Заголовок длиннее ${textSpec.title_max} символов`;
        }
        if ((form.text || '').length > textSpec.text_max) {
            return `Текст длиннее ${textSpec.text_max} символов`;
        }
        if ((form.cta_text || '').length > textSpec.cta_max) {
            return `Текст кнопки длиннее ${textSpec.cta_max} символов`;
        }
        if (!form.brand_name.trim()) {
            return 'Укажите название рекламодателя — этого требует закон о рекламе';
        }
        if (!form.landing_url.trim() && !form.deeplink.trim()) {
            return 'Укажите целевую ссылку';
        }
        if (isVideoFormat && !videoFile && !creative?.video_url) {
            return 'Для формата «видео за награду» нужен видеофайл';
        }
        if (!isVideoFormat && !imageFile && !creative?.image_url) {
            return 'Загрузите изображение';
        }
        return null;
    };

    const submit = async () => {
        const validationError = validate();
        if (validationError) { setError(validationError); return; }

        const fd = new FormData();
        [
            'format', 'title', 'text', 'cta_text', 'brand_name',
            'landing_url', 'deeplink',
            'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'weight',
        ].forEach((key) => {
            if (form[key] !== undefined && form[key] !== null && form[key] !== '') {
                fd.append(key, form[key]);
            }
        });
        if (imageFile) fd.append('image', imageFile);
        if (videoFile) fd.append('video', videoFile);
        // Длительность сервер из файла не достаёт — считаем её в браузере
        if (videoDuration) fd.append('video_duration_sec', videoDuration);

        try {
            setSaving(true);
            setError(null);
            if (isEdit) await APIAdsCreativeUpdate(token, creative.id, fd);
            else await APIAdsCreativeCreate(token, groupId, fd);
            onSaved?.();
            onClose?.();
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось сохранить объявление'));
        } finally {
            setSaving(false);
        }
    };

    const previewData = {
        ...form,
        image_url: previewUrl,
        image_wide_url: previewUrl,
        video_duration_sec: videoDuration || creative?.video_duration_sec,
    };

    return (
        <Modal open={open} onClose={saving ? undefined : onClose}>
            <ModalDialog sx={{ width: { xs: '96vw', md: 900 }, maxHeight: '92vh', overflowY: 'auto' }}>
                <Typography level="title-lg">
                    {isEdit ? 'Редактирование объявления' : 'Новое объявление'}
                </Typography>
                {isEdit && creative?.moderation_status === 'approved' && (
                    <Alert color="warning" size="sm" sx={{ mt: 1 }}>
                        Объявление уже одобрено. После правки содержимого оно вернётся
                        на модерацию, остальные объявления кампании продолжат показываться.
                    </Alert>
                )}

                {error && <Alert color="danger" sx={{ mt: 1.5 }}>{error}</Alert>}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} sx={{ mt: 2 }}>
                    {/* --- ФОРМА --- */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack spacing={1.5}>
                            <FormControl>
                                <FormLabel>Формат</FormLabel>
                                <Select
                                    value={form.format}
                                    onChange={(_, v) => v && set({ format: v })}
                                    disabled={isEdit}
                                >
                                    {allowedFormats.map((format) => (
                                        <Option key={format} value={format}>
                                            {FORMAT_LABELS[format] || format}
                                        </Option>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {isEdit
                                        ? 'Формат менять нельзя — создайте новое объявление'
                                        : 'Доступны форматы выбранных площадок кампании'}
                                </FormHelperText>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Заголовок</FormLabel>
                                <Input
                                    value={form.title}
                                    onChange={(e) => set({ title: e.target.value })}
                                    error={form.title.length > textSpec.title_max}
                                />
                                <CharCounter value={form.title} max={textSpec.title_max} />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Текст</FormLabel>
                                <Textarea
                                    minRows={2}
                                    value={form.text}
                                    onChange={(e) => set({ text: e.target.value })}
                                    error={(form.text || '').length > textSpec.text_max}
                                />
                                <CharCounter value={form.text} max={textSpec.text_max} />
                            </FormControl>

                            <Stack direction="row" spacing={1.5}>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Текст кнопки</FormLabel>
                                    <Input
                                        value={form.cta_text}
                                        onChange={(e) => set({ cta_text: e.target.value })}
                                        placeholder="Подробнее"
                                        error={(form.cta_text || '').length > textSpec.cta_max}
                                    />
                                    <CharCounter value={form.cta_text} max={textSpec.cta_max} />
                                </FormControl>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Вес в ротации</FormLabel>
                                    <Input
                                        type="number"
                                        value={form.weight}
                                        onChange={(e) => set({ weight: e.target.value })}
                                    />
                                    <FormHelperText>100 — обычный</FormHelperText>
                                </FormControl>
                            </Stack>

                            <FormControl>
                                <FormLabel>Название рекламодателя</FormLabel>
                                <Input
                                    value={form.brand_name}
                                    onChange={(e) => set({ brand_name: e.target.value })}
                                    placeholder="ООО «Ромашка»"
                                />
                                <FormHelperText>
                                    Обязательно по закону о рекламе, показывается в блоке
                                </FormHelperText>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Целевая ссылка</FormLabel>
                                <Input
                                    value={form.landing_url}
                                    onChange={(e) => set({ landing_url: e.target.value })}
                                    placeholder="https://example.com/promo"
                                />
                            </FormControl>

                            <Divider />

                            {/* --- МЕДИА --- */}
                            <Box>
                                <FormLabel sx={{ mb: 1 }}>
                                    {isVideoFormat ? 'Видео и обложка' : 'Изображение'}
                                </FormLabel>

                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                    <Button
                                        variant="outlined"
                                        color="neutral"
                                        startDecorator={<UploadRoundedIcon />}
                                        onClick={() => imageInputRef.current?.click()}
                                    >
                                        {isVideoFormat ? 'Обложка' : 'Изображение'}
                                    </Button>
                                    <input
                                        ref={imageInputRef}
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={(e) => handleImage(e.target.files?.[0])}
                                    />

                                    {isVideoFormat && (
                                        <>
                                            <Button
                                                variant="outlined"
                                                color="neutral"
                                                startDecorator={<UploadRoundedIcon />}
                                                onClick={() => videoInputRef.current?.click()}
                                            >
                                                Видеофайл
                                            </Button>
                                            <input
                                                ref={videoInputRef}
                                                type="file"
                                                accept="video/*"
                                                hidden
                                                onChange={(e) => handleVideo(e.target.files?.[0])}
                                            />
                                        </>
                                    )}
                                </Stack>

                                <Typography level="body-xs" color="neutral" sx={{ mt: 0.8 }}>
                                    {mediaSpec.image?.aspect
                                        ? `Пропорции ${mediaSpec.image.aspect}, от ${mediaSpec.image.min_width || 0} px, до ${mediaSpec.image.max_size_mb || 5} МБ`
                                        : 'Требования появятся после выбора формата'}
                                    {isVideoFormat && mediaSpec.video?.max_duration_sec
                                        ? ` · видео до ${mediaSpec.video.max_duration_sec} сек и ${mediaSpec.video.max_size_mb || 10} МБ`
                                        : ''}
                                </Typography>

                                {videoFile && (
                                    <Typography level="body-xs" color="success" sx={{ mt: 0.5 }}>
                                        Видео загружено: {videoFile.name}
                                        {videoDuration ? `, ${videoDuration} сек` : ''}
                                    </Typography>
                                )}

                                {mediaErrors.length > 0 && (
                                    <Alert color="danger" size="sm" sx={{ mt: 1 }}>
                                        <Box>
                                            {mediaErrors.map((text, i) => (
                                                <Typography key={i} level="body-sm">{text}</Typography>
                                            ))}
                                        </Box>
                                    </Alert>
                                )}
                                {mediaWarnings.length > 0 && (
                                    <Alert color="warning" size="sm" sx={{ mt: 1 }}>
                                        <Box>
                                            {mediaWarnings.map((text, i) => (
                                                <Typography key={i} level="body-sm">{text}</Typography>
                                            ))}
                                        </Box>
                                    </Alert>
                                )}
                            </Box>

                            <Divider />

                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                UTM-метки (необязательно)
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Input
                                    size="sm"
                                    placeholder="utm_source"
                                    value={form.utm_source}
                                    onChange={(e) => set({ utm_source: e.target.value })}
                                />
                                <Input
                                    size="sm"
                                    placeholder="utm_medium"
                                    value={form.utm_medium}
                                    onChange={(e) => set({ utm_medium: e.target.value })}
                                />
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Input
                                    size="sm"
                                    placeholder="utm_campaign"
                                    value={form.utm_campaign}
                                    onChange={(e) => set({ utm_campaign: e.target.value })}
                                />
                                <Input
                                    size="sm"
                                    placeholder="utm_content"
                                    value={form.utm_content}
                                    onChange={(e) => set({ utm_content: e.target.value })}
                                />
                            </Stack>
                        </Stack>
                    </Box>

                    {/* --- ПРЕДПРОСМОТР --- */}
                    <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
                        <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', position: 'sticky', top: 0 }}>
                            <Typography level="title-sm" sx={{ mb: 1.5 }}>
                                Так это увидит пользователь
                            </Typography>
                            <Stack alignItems="center">
                                <AdCreativePreview creative={previewData} format={form.format} />
                            </Stack>
                            <Typography level="body-xs" color="neutral" sx={{ mt: 1.5 }}>
                                Пометка «Реклама» и название рекламодателя добавляются
                                автоматически и убраны быть не могут.
                            </Typography>
                        </Sheet>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                    <Button variant="outlined" color="neutral" fullWidth onClick={onClose} disabled={saving}>
                        Отмена
                    </Button>
                    <Button fullWidth onClick={submit} loading={saving}>
                        {isEdit ? 'Сохранить' : 'Добавить объявление'}
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}